/* istanbul ignore file */
import React from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import { mapObject, constant, noop } from 'underscore';
import { withDefaultStyles } from '@bingads-webui-react/with-default-styles';
import { FallbackCallout, searchSiteMap } from '@bingads-webui-react/fallback-feature';
import { Glyphicon, MenuItem } from '@bingads-webui-react/primitive';
import { TooltipHost, TooltipOverflowMode, DirectionalHint } from '@bingads-webui-react/fabric-control';
import { StyledMenuItemTag } from '@bingads-webui-react/menu-item-tag';
import { siteMapPropType } from '@bingads-webui-react/with-site-map';
import { withScope, QueryParam } from '@bingads-webui-react/scope-service';
import { withRouter } from '@bingads-webui-react/router';
import { withUserConsumer } from '@bingads-webui-react/user-context';
import { withActivityContext } from '@bingads-webui-react/hoc-with-signal';
import { getMenuItemStyles } from '../../styles';
import { navigateBySiteMap, getNavigationObjectBySiteMap } from './utilities';

const BaMenuItemText = ({
  forwardedRef, text, classes, tooltipPlacement, directionalHint,
}) => (
  <TooltipHost
    hostClassName={classes.itemText}
    ref={forwardedRef}
    key={tooltipPlacement}
    overflowMode={TooltipOverflowMode.Self}
    directionalHint={directionalHint}
    content={text}
  >
    {text}
  </TooltipHost>
);


const BaMenuItemTextWithFallbackGlyph = ({
  text, showGlyph, classes, menuItemKey, tooltipPlacement, directionalHint,
}) => {
  const renderGlyph = () => (
    <Glyphicon
      className={classNames([
        classes.fallbackGlyph,
        'global-menu-toolbar-menu-item-fallback-glyph'.concat(menuItemKey)])}
      glyph="ba-OpenInNewWindow"
    />
  );

  return ((
    <React.Fragment>
      <BaMenuItemText
        classes={classes}
        text={text}
        tooltipPlacement={tooltipPlacement}
        directionalHint={directionalHint}
      />
      {showGlyph && renderGlyph()}
    </React.Fragment>
  ));
};


export class BaMenuItem extends React.PureComponent {
  constructor(props) {
    super(props);

    this.customizedOnClick = props.customizedOnClick;
    this.children = props.children;
    this.menuItemKey = props.menuItemKey;
    const {
      siteMap,
      siteMapId,
    } = this.props;

    this.externalLink = props.externalLink;
    if (siteMap) {
      const site = siteMapId ? searchSiteMap(siteMap, { id: siteMapId }) : false;
      // if its an external link set it to avlaible to avoid showing fallback glyph
      this.isPageEnabled = this.externalLink !== null ? true : site.available;
    } else {
      this.isPageEnabled = true;
    }

    this.state = {
      focused: false,
      hovered: false,
      showCallout: false,
    };

    // We want the fallback callout to be relative to the text,
    // but the click trigger should be on menu item. We can achieve this
    // passing the click event from the menu item to the text using this ref.
    this.clickRef = React.createRef();

    this.setFocused = this.setFocused.bind(this);
    this.setHovered = this.setHovered.bind(this);
    this.handleCalloutClose = this.handleCalloutClose.bind(this);
    this.handleClick = this.handleClick.bind(this);
  }

  setHovered = (value) => {
    this.setState({
      hovered: value,
    });
  };

  setFocused = (value) => {
    this.setState({
      focused: value,
    });
  };

  // React has a known issue where focus/hover events do not fire on unmount.
  // When the callout closes, we do not get an update that the focus has
  // been removed. So, we use this to manually handle the logic when
  // the callout closes.
  handleCalloutClose = (e) => {
    // This prevents the base menu item from triggering it's onClick
    if (e) {
      e.stopPropagation();
    }

    if (document.activeElement.id !== this.menuItemKey) {
      this.setState({
        focused: false,
        hovered: false,
        showCallout: false,
      });
    } else {
      this.setState({
        showCallout: false,
      });
    }
  };

  handleClick = (e, Id) => {
    if (this.props.activity) {
      this.props.activity.signal(`${Id} clicked`);
    }
    e.preventDefault();
    if (this.customizedOnClick) {
      this.customizedOnClick();
      this.props.closeDropdown();
      return;
    }
    if (this.isPageEnabled) {
      if (this.props.dismissMenu) {
        this.props.dismissMenu(null, true); // multilevel close all submenus.
      }
      if (this.externalLink) {
        window.open(this.externalLink);
      } else {
        const {
          scope,
          siteMap,
          siteMapId,
          history,
          location,
          userContext,
        } = this.props;
        navigateBySiteMap({
          scope,
          siteMap,
          siteMapId,
          history,
          location,
          userContext,
        });
      }
      this.props.closeDropdown();
    } else {
      this.setState(state => ({
        showCallout: !state.showCallout,
      }));
    }
  };

  render() {
    const showFallbackGlyph = (this.state.focused
      || this.state.hovered
      || this.state.showCallout) && !this.isPageEnabled && this.externalLink === null;

    const {
      calloutPlacement,
      siteMap,
      siteMapId,
      scope,
      userContext,
      showTag,
      tagClass,
      i18n,
      customizedClass,
      classes,
      headerId,
    } = this.props;

    let navigationObject;
    if (siteMap) {
      navigationObject = getNavigationObjectBySiteMap({
        scope,
        siteMap,
        siteMapId,
        userContext,
        getAbsoluteIfExists: true,
      });
    }
    let href = '#';
    if (this.externalLink) {
      href = this.externalLink;
    } else if (navigationObject) {
      // eslint-disable-next-line prefer-destructuring
      href = navigationObject.href;
    }

    return (
      <MenuItem
        id={this.menuItemKey}
        role="presentation"
        className={classNames([
          classes.container,
          classes.toggleable,
          customizedClass,
          'global-menu-toolbar-menu-item'.concat(this.menuItemKey),
        ])}
        onClick={e => this.handleClick(e, this.menuItemKey)}
        href={href}
        onMouseEnter={() => this.setHovered(true)}
        onMouseLeave={() => this.setHovered(false)}
        onFocus={() => this.setFocused(true)}
        onBlur={() => this.setFocused(false)}
        aria-labelledby={`${headerId} ${this.menuItemKey}`}
      >
        <div
          className={classes.item}
        >
          <FallbackCallout
            title={this.children}
            classes={{ calloutTargetContainer: classes.calloutTargetContainer }}
            handleClose={this.handleCalloutClose}
            getOldUIPathOverride={this.props.getOldUIPathOverride}
            show={this.state.showCallout}
            placement={calloutPlacement}
            siteMapId={this.props.siteMapId}
          >
            <span ref={this.clickRef} className={'global-menu-toolbar-menu-item-callout-click'.concat(this.menuItemKey)}>
              <BaMenuItemTextWithFallbackGlyph
                classes={classes}
                text={this.children}
                showGlyph={showFallbackGlyph}
                menuItemKey={this.menuItemKey}
                tooltipPlacement={this.props.tooltipPlacement}
                directionalHint={this.props.directionalHint}
              />
            </span>
          </FallbackCallout>
          {
            showTag ? // preserve this check for better performance
              <div className={classes.menuItemTagContainer}>
                <StyledMenuItemTag tagContent={showTag} tagClass={tagClass} i18n={i18n} />
              </div> : ''
          }
        </div>
      </MenuItem>
    );
  }
}

BaMenuItemText.propTypes = {
  forwardedRef: PropTypes.shape({ current: PropTypes.instanceOf(Element) }),
  classes: PropTypes.shape(mapObject(
    getMenuItemStyles({}, {}),
    constant(PropTypes.string.isRequired)
  )).isRequired,
  text: PropTypes.string.isRequired,
  tooltipPlacement: PropTypes.string.isRequired,
  directionalHint: PropTypes.number.isRequired,
};

BaMenuItemText.defaultProps = {
  forwardedRef: null,
};

BaMenuItemTextWithFallbackGlyph.propTypes = {
  classes: PropTypes.shape(mapObject(
    getMenuItemStyles({}, {}),
    constant(PropTypes.string.isRequired)
  )).isRequired,
  showGlyph: PropTypes.bool.isRequired,
  text: PropTypes.string.isRequired,
  menuItemKey: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.number,
  ]).isRequired,
  tooltipPlacement: PropTypes.string.isRequired,
  directionalHint: PropTypes.number.isRequired,
};

BaMenuItem.propTypes = {
  classes: PropTypes.shape(mapObject(
    getMenuItemStyles({}, {}),
    constant(PropTypes.string.isRequired)
  )).isRequired,
  customizedClass: PropTypes.string,
  customizedOnClick: PropTypes.func,
  menuItemKey: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.number,
  ]).isRequired,
  children: PropTypes.string.isRequired,
  getOldUIPathOverride: PropTypes.func,
  siteMapId: PropTypes.string,
  siteMap: siteMapPropType,
  scope: PropTypes.instanceOf(QueryParam).isRequired,
  calloutPlacement: PropTypes.oneOf([
    'left',
    'right',
  ]),
  externalLink: PropTypes.string,
  tooltipPlacement: PropTypes.string,
  closeDropdown: PropTypes.func,
  dismissMenu: PropTypes.func,
  directionalHint: PropTypes.number,
  showTag: PropTypes.oneOfType([PropTypes.string, PropTypes.shape({})]),
  tagClass: PropTypes.string,
  history: PropTypes.shape({
    push: PropTypes.func,
  }).isRequired,
  location: PropTypes.shape({
    pathname: PropTypes.string,
  }).isRequired,
  userContext: PropTypes.shape({
    CurrentAccount: PropTypes.shape({}),
  }),
  i18n: PropTypes.shape({
    getString: PropTypes.func,
  }),
  activity: PropTypes.shape({
    signal: PropTypes.func,
  }),
  headerId: PropTypes.string,
};


BaMenuItem.defaultProps = {
  customizedClass: '',
  getOldUIPathOverride: null,
  siteMapId: null,
  calloutPlacement: 'right',
  externalLink: null,
  tooltipPlacement: 'bottom',
  customizedOnClick: null,
  closeDropdown: () => { },
  dismissMenu: () => { },
  siteMap: null,
  directionalHint: DirectionalHint.bottomCenter,
  showTag: '',
  tagClass: '',
  userContext: {},
  i18n: null,
  activity: {
    signal: noop,
  },
  headerId: undefined,
};

export const StyledBaMenuItem
  = withActivityContext(withUserConsumer(withRouter(withScope(withDefaultStyles(
    BaMenuItem,
    getMenuItemStyles
  )))));
